import React, { useState, useCallback } from 'react';
import { Record, Vendor, POC, DistroList, FleetEntry, Report } from '../types';
import { ApiError } from '../api';
import {
  getRecords,
  getVendors,
  getPocs,
  getDistros,
  getFleet,
  getReports,
} from '../api';
import { showErrorToast } from '../utils/toast';
import { logger } from '../utils/logger';

/**
 * Wrap an async operation with error handling and user feedback.
 * Returns the result on success, or undefined on failure.
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  context: string,
): Promise<T | undefined> {
  try {
    return await operation();
  } catch (err) {
    if (err instanceof ApiError) {
      logger.error('API operation failed', { context, statusCode: err.statusCode, errorMessage: err.message });
      if (err.statusCode === 401 || err.statusCode === 403) {
        showErrorToast('You do not have permission to perform this action.');
      } else if (err.statusCode >= 500) {
        showErrorToast(`Server error while ${context.toLowerCase()}. Please try again later.`);
      } else {
        showErrorToast(`Failed: ${context}. (${err.statusCode})`);
      }
    } else {
      logger.error('Unexpected error during operation', { context, error: String(err) });
      showErrorToast(`An unexpected error occurred while ${context.toLowerCase()}.`);
    }
    return undefined;
  }
}

/** Application data state returned by useDataFetching. */
export interface DataState {
  records: Record[];
  vendors: Vendor[];
  pocs: POC[];
  distros: DistroList[];
  fleet: FleetEntry[];
  reports: Report[];
  fetchRecords: () => Promise<void>;
  fetchVendors: () => Promise<void>;
  fetchPocs: () => Promise<void>;
  fetchDistros: () => Promise<void>;
  fetchFleet: () => Promise<void>;
  fetchReports: () => Promise<void>;
}

/**
 * Creates a memoized async fetch callback that uses withErrorHandling
 * and updates state on success.
 */
function useFetchCallback<T>(
  apiFn: () => Promise<T>,
  setter: React.Dispatch<React.SetStateAction<T>>,
  context: string,
): () => Promise<void> {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useCallback(async (): Promise<void> => {
    try {
      const data = await withErrorHandling(apiFn, context);
      if (data) setter(data);
    } catch (err) {
      logger.error(`Failed to fetch ${context.toLowerCase()}`, { error: String(err) });
    }
  }, [apiFn, setter, context]);
}

/**
 * Custom hook that manages all data fetching for the LOFT application.
 * Encapsulates records, vendors, POCs, distribution lists, and fleet data.
 */
export function useDataFetching(): DataState {
  const [records, setRecords] = useState<Record[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [pocs, setPocs] = useState<POC[]>([]);
  const [distros, setDistros] = useState<DistroList[]>([]);
  const [fleet, setFleet] = useState<FleetEntry[]>([]);
  const [reports, setReports] = useState<Report[]>([]);

  const fetchRecords = useFetchCallback(getRecords, setRecords, 'Loading records');
  const fetchVendors = useFetchCallback(getVendors, setVendors, 'Loading vendors');
  const fetchPocs = useFetchCallback(getPocs, setPocs, 'Loading POCs');
  const fetchDistros = useFetchCallback(getDistros, setDistros, 'Loading distribution lists');
  const fetchFleet = useFetchCallback(getFleet, setFleet, 'Loading fleet data');
  const fetchReports = useFetchCallback(getReports, setReports, 'Loading reports');

  return {
    records,
    vendors,
    pocs,
    distros,
    fleet,
    reports,
    fetchRecords,
    fetchVendors,
    fetchPocs,
    fetchDistros,
    fetchFleet,
    fetchReports,
  };
}
